import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, TextField, Typography, Chip } from "@mui/material";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import { toast } from "react-toastify";

import palette from "../../../common/styles/palette";
import SheAiChecklistService from "../../../services/sheai/SheAiChecklistService";
import { AiItemFeedback } from "../../../services/sheai/SheAiChecklistTypes";

export type ParecerAction = "agree" | "partial" | "disagree" | "complement";

interface AuditoriaParecerProps {
  /** Identificador estável do ponto auditado (ex.: `soft_facial`, `turn_12::facial`, item_id do checklist). */
  criterioId: string | number;
  debriefingId?: string | number | null;
  /** Feedback já persistido no backend para este critério. */
  feedback?: AiItemFeedback | null;
  /** Rótulo do campo de texto. */
  label?: string;
  placeholder?: string;
  /** Exibe os botões Concorda/Discorda acima do campo. */
  comVeredito?: boolean;
  /** Layout enxuto para uso dentro de cards de faceta. */
  compacto?: boolean;
  /** Notificado sempre que o parecer é persistido (para recalcular contadores). */
  onSalvo?: (criterioId: string, feedback: AiItemFeedback) => void;
}

/**
 * Campo de parecer do professor reutilizável.
 *
 * Regras de produto (BHCBAI — Dossiê 360°):
 * - o parecer é SEMPRE opcional: um dossiê pode ser submetido com itens em branco;
 * - nada é enviado ao backend enquanto o professor não escrever/decidir algo —
 *   assim campos em branco não poluem o `professor_guidance` da reanálise;
 * - o salvamento acontece no blur (sem botão), para não quebrar o fluxo de
 *   auditoria de dezenas de pontos.
 */
export const AuditoriaParecer: React.FC<AuditoriaParecerProps> = ({
  criterioId,
  debriefingId,
  feedback,
  label = "Parecer do Professor (opcional)",
  placeholder = "Escreva sua crítica ou deixe em branco…",
  comVeredito = false,
  compacto = false,
  onSalvo,
}) => {
  const criterioStr = String(criterioId);
  const [texto, setTexto] = useState<string>(feedback?.note || "");
  const [action, setAction] = useState<ParecerAction | undefined>(feedback?.action);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    setTexto(feedback?.note || "");
    setAction(feedback?.action);
  }, [feedback?.note, feedback?.action]);

  const persistir = async (novaAction?: ParecerAction, novoTexto?: string) => {
    const nota = novoTexto !== undefined ? novoTexto : texto;
    const decisao = novaAction !== undefined ? novaAction : action;

    // Nada preenchido = item deliberadamente em branco. Não vai para o backend.
    if (!nota.trim() && !decisao) return;
    // Sem alteração real em relação ao que já está salvo.
    if ((feedback?.note || "") === nota && feedback?.action === decisao) return;
    if (!debriefingId) return;

    setSalvando(true);
    try {
      await SheAiChecklistService.postChecklistFeedback({
        debriefing_id: debriefingId,
        item_id: criterioStr,
        action: decisao,
        note: nota,
      });
      if (onSalvo) onSalvo(criterioStr, { action: decisao, note: nota });
    } catch (e) {
      toast.error("Não foi possível salvar o parecer deste ponto.");
    } finally {
      setSalvando(false);
    }
  };

  const escolherVeredito = (nova: ParecerAction) => {
    const proxima = action === nova ? undefined : nova;
    setAction(proxima);
    if (proxima) persistir(proxima);
  };

  return (
    <Box sx={{ mt: compacto ? 1 : 1.5 }}>
      {comVeredito && (
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", mb: 0.75, flexWrap: "wrap" }}>
          <RateReviewOutlinedIcon sx={{ fontSize: 16, color: palette.textMuted }} />
          <Button
            size="small"
            variant={action === "agree" ? "contained" : "outlined"}
            onClick={() => escolherVeredito("agree")}
            sx={{ fontSize: "11px", py: 0.2, px: 1, minWidth: "auto", textTransform: "none", fontWeight: 600 }}
          >
            ✓ Concorda
          </Button>
          <Button
            size="small"
            variant={action === "partial" ? "contained" : "outlined"}
            color="warning"
            onClick={() => escolherVeredito("partial")}
            sx={{ fontSize: "11px", py: 0.2, px: 1, minWidth: "auto", textTransform: "none", fontWeight: 600 }}
          >
            ~ Parcial
          </Button>
          <Button
            size="small"
            variant={action === "disagree" ? "contained" : "outlined"}
            color="error"
            onClick={() => escolherVeredito("disagree")}
            sx={{ fontSize: "11px", py: 0.2, px: 1, minWidth: "auto", textTransform: "none", fontWeight: 600 }}
          >
            ✕ Discorda
          </Button>
          {!action && !texto.trim() && (
            <Chip
              label="Em branco"
              size="small"
              sx={{ height: 18, fontSize: "10px", color: palette.textMuted, backgroundColor: "#F1F5F9" }}
            />
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          size="small"
          fullWidth
          multiline={!compacto}
          minRows={compacto ? undefined : 2}
          label={compacto ? undefined : label}
          placeholder={placeholder}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onBlur={() => persistir(undefined, texto)}
          inputProps={{ "aria-label": `Parecer do professor para ${criterioStr}` }}
          sx={{
            "& .MuiInputBase-input": { fontSize: compacto ? "11.5px" : "13px" },
            backgroundColor: "#FFFFFF",
          }}
        />
        {salvando && <CircularProgress size={14} />}
      </Box>

      {!compacto && (
        <Typography variant="caption" sx={{ color: palette.textMuted, display: "block", mt: 0.5 }}>
          Opcional — o dossiê pode ser entregue com pontos sem parecer.
        </Typography>
      )}
    </Box>
  );
};

export default AuditoriaParecer;
